import React, { useState } from "react";
import { Package, ArrowRight, Trash2, Plus } from "lucide-react";
import { Item, Model, ItemTemplate, Warband } from "../types";
import { ItemEditor } from "../ItemEditor";

interface WarchestProps {
  warchestItems: Item[];
  models: Model[];
  warband: Warband;
  onMoveToModel: (itemId: string, modelId: string) => void;
  onRemoveItem: (itemId: string) => void;
  onAddItem?: (item: Item) => void;
  itemTemplates?: ItemTemplate[];
  onUpdateItemTemplates?: (templates: ItemTemplate[]) => void;
}

export const Warchest = ({
  warchestItems,
  models,
  warband,
  onMoveToModel,
  onRemoveItem,
  onAddItem,
  itemTemplates,
  onUpdateItemTemplates
}: WarchestProps) => {
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showItemEditor, setShowItemEditor] = useState(false);

  const handleMoveToModel = (itemId: string) => {
    setSelectedItem(itemId);
    setShowMoveModal(true);
  };

  const handleConfirmMove = (modelId: string) => {
    if (selectedItem) {
      onMoveToModel(selectedItem, modelId);
      setSelectedItem(null);
      setShowMoveModal(false);
    }
  };

  const handleAddItem = (newItem: Item) => {
    if (onAddItem) {
      onAddItem(newItem);
    }
    setShowItemEditor(false);
  };

  const aliveModels = models.filter(m => m.alive);

  return (
    <div className="bg-[#E6E5D3] p-3 text-sm text-[#111]">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-3">
          <Package size={18} className="text-[#111]" />
          <h2 className="text-base font-semibold text-[#111] uppercase tracking-wider">
            Warchest ({warchestItems.length})
          </h2>
        </div>
        {onAddItem && (
          <button
            onClick={() => setShowItemEditor(true)}
            className="bg-[#6b1c1c] hover:bg-[#5c1414] text-[#E6E5D3] px-3 py-2 flex items-center gap-2 uppercase tracking-wider tutorial-add-item text-xs font-semibold"
          >
            <Plus size={16} />
            Add Item
          </button>
        )}
      </div>

      {warchestItems.length === 0 ? (
        <div className="text-center py-8">
          <Package size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-sm uppercase tracking-wide">
            No items in warchest
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Items will be discarded when reinforcing
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2">
          {warchestItems.map((item) => (
            <div
              key={item.id}
              className="bg-[#f1f0e0] p-3 transition-colors"
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-sm font-semibold text-[#111] uppercase tracking-wider">
                  {item.name}
                </h3>
                <button
                  onClick={() => onRemoveItem(item.id)}
                  className="text-[#6b1c1c] hover:text-[#5c1414] p-1"
                  title="Remove item from warchest"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="text-xs text-[#111] space-y-1">
                <p><span className="text-[#777]">Category:</span> {item.category}</p>
                {item.keywords.length > 0 && (
                  <p><span className="text-[#777]">Keywords:</span> {item.keywords.join(", ")}</p>
                )}
                {item.range && (
                  <p><span className="text-[#777]">Range:</span> {item.range}</p>
                )}
                {item.armourModifier && (
                  <p><span className="text-[#777]">Armour:</span> {item.armourModifier}</p>
                )}
                {item.notes && (
                  <p className="text-xs text-[#777] mt-2">{item.notes}</p>
                )}
              </div>

              <div className="flex gap-2 mt-3">
                {aliveModels.length > 0 && (
                  <button
                    onClick={() => handleMoveToModel(item.id)}
                    className="flex-1 bg-[#222] hover:bg-[#1a1a1a] text-[#E6E5D3] px-3 py-2 text-xs flex items-center justify-center gap-1 tutorial-move-to-model"
                  >
                    <ArrowRight size={14} />
                    Move to Model
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Move to Model Modal */}
      {showMoveModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4 uppercase tracking-wider">
              Move Item to Model
            </h3>

            <div className="space-y-2 mb-6">
              {aliveModels.map((model) => (
                <button
                  key={model.id}
                  onClick={() => handleConfirmMove(model.id)}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-white px-4 py-3 rounded flex items-center justify-between"
                >
                  <span>{model.name} ({model.class})</span>
                  <ArrowRight size={16} />
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowMoveModal(false)}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Editor Modal */}
      {showItemEditor && (
        <ItemEditor
          item={null}
          onSave={handleAddItem}
          onCancel={() => setShowItemEditor(false)}
          isTemplateMode={false}
          allItemTemplates={itemTemplates || []}
          updateItemTemplates={onUpdateItemTemplates || (() => {})}
          warband={warband}
          models={models}
          items={warchestItems}
        />
      )}
    </div>
  );
};

